import {
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Item,
  ItemContent,
  ItemDescription,
  ItemTitle,
  Label,
} from "@beweegbaas/ui";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useMemo, useState } from "react";
import { toast } from "@beweegbaas/ui";
import { FormErrorSummary } from "#/components/form-errors";
import { SummaryInformation } from "#/modules/school.import/components/import-summary";
import { ImportReviewContext } from "#/modules/school.import/import-review-context";
import {
  getImportSubmitData,
  submitImportStudents,
} from "#/modules/school.import/import.functions";
import { useImportSummary } from "#/modules/school.import/use-import-summary";
import { op } from "#/modules/util.instrumentation/op";
import { m } from "#/paraglide/messages";

export const Route = createFileRoute(
  "/$locale/{-$schoolyear}/_authenticated/$school/detail/import/$type/review/submit",
)({
  loader: ({ params }) =>
    getImportSubmitData({
      data: { school: params.school, type: params.type, locale: params.locale },
    }),
  component: ImportSubmitPage,
});

function ImportSubmitPage() {
  const loaderData = Route.useLoaderData();
  const { locale, school, type } = Route.useParams();
  const submitImportFn = useServerFn(submitImportStudents);
  const router = useRouter();

  return (
    <ImportReviewContext.Provider value={loaderData}>
      <ImportSubmitForm
        onSubmit={async () => {
          const result = await submitImportFn({ data: { locale, school, type } });
          if (result && "success" in result && !result.success) {
            void op.track("import_failed", {
              step: "submit",
              type,
              school,
              reason: result.reason ?? "Unknown",
              status: "status" in result ? (result.status ?? undefined) : undefined,
            });
            return result;
          }
          toast.success(m["import.succeeded"]());
          void op.track("import_completed", { type, school });
          await router.invalidate();
          return null;
        }}
      />
    </ImportReviewContext.Provider>
  );
}

function ImportSubmitForm({
  onSubmit,
}: {
  onSubmit: () => Promise<{ formErrors?: string[] } | null>;
}) {
  const { importPreferences } = Route.useLoaderData();
  const { type } = Route.useParams();
  const summary = useImportSummary();
  const [formErrors, setFormErrors] = useState<string[] | undefined>();
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [isAcknowledgedNewSchoolYear, setIsAcknowledgedNewSchoolYear] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const showDeletionsWarning = importPreferences.allowDeletes && summary.hasDeletions;
  const showNewSchoolYearWarning = type === "start-new-year";

  const allowSubmit = useMemo(() => {
    if (showDeletionsWarning && showNewSchoolYearWarning)
      return isAcknowledged && isAcknowledgedNewSchoolYear;
    if (showDeletionsWarning) return isAcknowledged;
    if (showNewSchoolYearWarning) return isAcknowledgedNewSchoolYear;
    return true;
  }, [isAcknowledged, isAcknowledgedNewSchoolYear, showDeletionsWarning, showNewSchoolYearWarning]);

  async function handleConfirm() {
    if (!allowSubmit || isSubmitting) return;
    setIsSubmitting(true);
    try {
      const result = await onSubmit();
      if (result?.formErrors) setFormErrors(result.formErrors);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <FormErrorSummary errors={formErrors} className="mb-2" />
      {!summary.hasInvalidItems && (
        <Card>
          <CardHeader>
            <CardTitle>{m["import.review.all_correct"]()}</CardTitle>
          </CardHeader>
          <CardContent>
            <SummaryInformation />
          </CardContent>
        </Card>
      )}
      {summary.hasInvalidItems && (
        <Alert variant="destructive">
          <AlertTitle>{m["import.review.errors_found"]()}</AlertTitle>
          <AlertDescription>
            <ul className="space-y-2">
              {summary.invalid.students > 0 && (
                <li>{m["import.review.invalid_students"]({ count: summary.invalid.students })}</li>
              )}
              {summary.invalid.classes > 0 && (
                <li>{m["import.review.invalid_classes"]({ count: summary.invalid.classes })}</li>
              )}
            </ul>
          </AlertDescription>
        </Alert>
      )}
      <div className="mt-6 flex items-start justify-end gap-4">
        <AlertDialog>
          <AlertDialogTrigger
            render={<Button type="button" size="xl" disabled={summary.hasInvalidItems} />}
          >
            {m["common.actions.importeren"]()}
          </AlertDialogTrigger>
          <AlertDialogContent>
            <FormErrorSummary errors={formErrors} />
            <AlertDialogHeader>
              <AlertDialogTitle className="mb-2">
                {m["import.review.confirm_import"]()}
              </AlertDialogTitle>
              <AlertDialogDescription className="flex w-full flex-col gap-4">
                {showDeletionsWarning && (
                  <Item variant="destructive">
                    <ItemContent>
                      <ItemTitle>{m["import.review.warning_items_removed"]()}</ItemTitle>
                      <ItemDescription>
                        <Label className="mt-3 flex cursor-pointer items-center gap-2">
                          <Checkbox
                            checked={isAcknowledged}
                            onCheckedChange={(c) => setIsAcknowledged(c === true)}
                          />
                          <span>{m["import.review.i_understand"]()}</span>
                        </Label>
                      </ItemDescription>
                    </ItemContent>
                  </Item>
                )}
                {showNewSchoolYearWarning && (
                  <Label className="mt-3 flex cursor-pointer items-center gap-2">
                    <Checkbox
                      checked={isAcknowledgedNewSchoolYear}
                      onCheckedChange={(c) => setIsAcknowledgedNewSchoolYear(c === true)}
                    />
                    <span>{m["import.review.i_understand"]()}</span>
                  </Label>
                )}
                <p>{m["import.review.confirmation"]()}</p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel variant="outline" size="default">
                {m["common.actions.cancel"]()}
              </AlertDialogCancel>
              <AlertDialogAction
                variant="default"
                onClick={handleConfirm}
                disabled={!allowSubmit}
                isPending={isSubmitting}
              >
                {m["import.review.start_import"]()}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </>
  );
}
